import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { X, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AccountType } from '../types';

// Blocks access to provider-only routes until workforce intake is complete.
//
// Three states, derived from workforceIntakeCompleted + workforceIntakeDeadline:
//   - NONE:         intake already done -> render children normally
//   - GRACE_PERIOD: deadline is set and still in the future -> render children +
//                    dismissible banner (existing providers get a warning window)
//   - HARD:         deadline is null (new provider, no grace period) OR deadline
//                    has passed -> hard block, redirect to /workforce-intake
//
// Mirrors the backend's GateStatusResponse.of() logic in
// WorkforceIntakeController -- keep both in sync if either changes.
const ProviderGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isVerified } = useAuth();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const isVerifiedProvider = user?.accountType === AccountType.PROVIDER && isVerified;
  const intakeDone = !!user?.workforceIntakeCompleted;

  if (!isVerifiedProvider || intakeDone) {
    return <>{children}</>;
  }

  const deadline = user?.workforceIntakeDeadline ? new Date(user.workforceIntakeDeadline) : null;
  const now = new Date();
  const inGracePeriod = !!deadline && deadline > now;

  if (!inGracePeriod) {
    // deadline is null (new provider) or has passed (existing provider, grace period over)
    return <Navigate to="/workforce-intake" replace />;
  }

  const daysRemaining = Math.max(0, Math.ceil((deadline!.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  return (
    <>
      {!bannerDismissed && (
        <div className="bg-brand-50 border-b border-brand-200 px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-brand-600 flex-shrink-0" />
            <p className="text-sm text-slate-700">
              <span className="font-semibold">New requirement:</span> please complete your workforce profile
              within {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}.{' '}
              <a href="#/workforce-intake" className="font-semibold text-brand-700 underline">
                Complete now
              </a>
            </p>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {children}
    </>
  );
};

export default ProviderGate;
